import { useState } from "react";
import { MapPin, Phone, Mail, Clock, Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import { CONTACT } from "@/components/PublicLayout";
import { WhatsAppCTA } from "@/components/WhatsApp";
import { maskPhone } from "@/lib/format";

const INFO = [
  { icon: MapPin, label: "Endereço", value: CONTACT.address },
  { icon: Phone, label: "Telefone / WhatsApp", value: `${CONTACT.phone} · ${CONTACT.whatsapp}` },
  { icon: Mail, label: "E-mail", value: CONTACT.email },
  { icon: Clock, label: "Horário", value: CONTACT.hours },
];

export default function Contato() {
  const [form, setForm] = useState({ nome: "", email: "", telefone: "", assunto: "Orçamento", mensagem: "" });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    await new Promise((r) => setTimeout(r, 800));
    toast.success("Mensagem registrada! Retornaremos em até 48h úteis.");
    setForm({ nome: "", email: "", telefone: "", assunto: "Orçamento", mensagem: "" });
    setLoading(false);
  };

  const inputCls =
    "mt-1.5 w-full bg-zinc-900/70 border border-zinc-700 rounded-sm px-3 py-2.5 text-sm outline-none focus:border-orange-500 transition-colors placeholder:text-zinc-600";

  return (
    <div className="pt-[72px]" data-testid="contato-page">
      <section className="py-20 border-b border-zinc-800 bg-zinc-900/40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex items-center gap-3 mb-3">
            <span className="h-px w-10 bg-orange-500" />
            <span className="mono text-xs uppercase tracking-[0.35em] text-orange-400">fale conosco</span>
          </div>
          <h1 className="display text-4xl sm:text-6xl font-extrabold uppercase">Contato</h1>
          <p className="mt-4 text-zinc-400 max-w-2xl text-sm sm:text-base">
            Envie seu projeto ou tire dúvidas com nossa equipe técnica. Resposta em até 48 horas úteis.
          </p>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 grid lg:grid-cols-5 gap-10">
          <div className="lg:col-span-2 space-y-4">
            {INFO.map((i) => (
              <div key={i.label} className="card-tactical rounded-md p-5 flex items-start gap-4" data-testid={`contact-info-${i.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                <div className="shrink-0 w-10 h-10 grid place-content-center border border-zinc-700 rounded-sm bg-zinc-900">
                  <i.icon className="w-5 h-5 text-orange-500" strokeWidth={1.5} />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-zinc-500">{i.label}</div>
                  <div className="mt-1 text-sm text-zinc-200 leading-relaxed">{i.value}</div>
                </div>
              </div>
            ))}
            <div className="rounded-md border border-zinc-800 overflow-hidden h-56 grid place-content-center bg-zinc-900/60 grid-bg" data-testid="contact-map-placeholder">
              <div className="text-center">
                <MapPin className="w-8 h-8 text-orange-500 mx-auto" />
                <div className="mono mt-2 text-xs uppercase tracking-widest text-zinc-500">mapa em breve</div>
              </div>
            </div>
            <WhatsAppCTA message="Olá! Gostaria de solicitar um orçamento." testId="contact-whatsapp-cta" className="w-full" />
          </div>

          <form onSubmit={submit} className="lg:col-span-3 card-tactical rounded-md p-7 sm:p-9" data-testid="contact-form">
            <h2 className="display text-2xl font-bold uppercase tracking-wide">Solicite um orçamento</h2>
            <p className="mt-1 text-sm text-zinc-500">Aceitamos arquivos DXF, PDF e STEP — anexe pelo e-mail após o primeiro contato.</p>

            <div className="mt-6 grid sm:grid-cols-2 gap-5">
              <div>
                <label className="text-xs uppercase tracking-wider text-zinc-400">Nome *</label>
                <input data-testid="contact-name-input" required value={form.nome} onChange={set("nome")} className={inputCls} placeholder="Seu nome" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-zinc-400">E-mail *</label>
                <input data-testid="contact-email-input" type="email" required value={form.email} onChange={set("email")} className={inputCls} placeholder="voce@empresa.com.br" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-zinc-400">Telefone</label>
                <input data-testid="contact-phone-input" inputMode="tel" value={form.telefone} onChange={(e) => setForm({ ...form, telefone: maskPhone(e.target.value) })} className={inputCls} placeholder="(00) 00000-0000" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-zinc-400">Assunto</label>
                <select data-testid="contact-subject-select" value={form.assunto} onChange={set("assunto")} className={inputCls}>
                  <option>Orçamento</option>
                  <option>Dúvida técnica</option>
                  <option>Acompanhamento de pedido</option>
                  <option>Outro</option>
                </select>
              </div>
            </div>

            <div className="mt-5">
              <label className="text-xs uppercase tracking-wider text-zinc-400">Mensagem *</label>
              <textarea
                data-testid="contact-message-input"
                required
                rows={5}
                value={form.mensagem}
                onChange={set("mensagem")}
                className={`${inputCls} resize-none`}
                placeholder="Descreva a peça, material, quantidade e prazo desejado…"
              />
            </div>

            <button
              data-testid="contact-submit-button"
              type="submit"
              disabled={loading}
              className="mt-7 inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-500 disabled:opacity-60 text-white font-semibold uppercase tracking-wider px-8 py-3.5 rounded-sm transition-colors"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              Enviar mensagem
            </button>
          </form>
        </div>
      </section>
    </div>
  );
}
